import { paths } from '@/routes/paths';

export type TourRole = 'staff' | 'cliente';

export interface TourStep {
  element: string;
  title: string;
  description: string;
  side: 'top' | 'bottom' | 'left' | 'right';
  align: 'start' | 'center' | 'end';
  /**
   * Page this step belongs to. The tour navigates here before highlighting,
   * so a stop is not dropped just because the user started somewhere else.
   */
  path?: string;
}

const operativa = paths.admin.operativa;
const alert = paths.admin.backup.alert;
const documentazione = paths.admin.documentazioneInterna;
const documentiStaff = paths.admin.documenti;
const documentiCliente = paths.documenti.root;

/** Staff tour walks the four pages used every day, and says what changes at each one. */
export const TOUR_STAFF: TourStep[] = [
  {
    path: operativa,
    element: '[data-tour="sidebar"]',
    title: 'Navigazione principale',
    description:
      'Da qui passi tra le aree. Il tour ti porta sulla dashboard operativa, poi su alert, documentazione e documenti.',
    side: 'right',
    align: 'start',
  },
  {
    path: operativa,
    element: '[data-tour="search"]',
    title: 'Cerca qualsiasi cosa',
    description: 'Cerca clienti, ticket, contratti o vai direttamente a una pagina. Scorciatoia: Ctrl+K.',
    side: 'bottom',
    align: 'center',
  },
  {
    path: operativa,
    element: '[data-tour="new-ticket"]',
    title: 'Nuovo ticket',
    description: 'Crea un ticket in qualsiasi momento. Puoi assegnarlo a te o lasciarlo in coda.',
    side: 'bottom',
    align: 'end',
  },
  {
    path: operativa,
    element: '[data-tour="da-iniziare"]',
    title: 'Dashboard operativa',
    description:
      'Il lavoro di oggi: le tue attività, quelle dei colleghi e quelle ancora senza nessuno. Non è lo stato delle macchine.',
    side: 'bottom',
    align: 'center',
  },
  {
    path: operativa,
    element: '[data-tour="tickets-table"]',
    title: 'Ticket aperti',
    description: 'La coda delle richieste. Un ticket è un lavoro da seguire, non un allarme tecnico.',
    side: 'top',
    align: 'center',
  },
  {
    path: operativa,
    element: '[data-tour="calendar"]',
    title: 'Calendario',
    description: 'Appuntamenti, scadenze e attività condivise tra operatori.',
    side: 'bottom',
    align: 'center',
  },
  {
    path: alert,
    element: '[data-tour="alert"]',
    title: 'Alert',
    description:
      'Cosa cambia: qui non ci sono ticket. Vedi le macchine in errore, warning o silenzio, su backup, firewall, antivirus e syslog.',
    side: 'bottom',
    align: 'start',
  },
  {
    path: documentazione,
    element: '[data-tour="documentazione"]',
    title: 'Documentazione interna',
    description:
      'Cosa cambia: dal monitoraggio alle note del cliente. File e link restano solo nello staff e non compaiono nel portale.',
    side: 'bottom',
    align: 'start',
  },
  {
    path: documentiStaff,
    element: '[data-tour="documenti"]',
    title: 'Documenti condivisi',
    description:
      'Cosa cambia: questi file possono uscire verso il cliente. Qui decidi chi li vede. La documentazione interna, invece, resta chiusa.',
    side: 'bottom',
    align: 'start',
  },
  {
    path: documentiStaff,
    element: '[data-tour="profile-menu"]',
    title: 'Il tuo profilo',
    description: 'Impostazioni, notifiche e il comando per rivedere questo tour, da qualunque pagina.',
    side: 'bottom',
    align: 'end',
  },
];

/** Customer admins start on the service overview, then the files shared with the company. */
export const TOUR_CLIENTE: TourStep[] = [
  {
    path: paths.dashboard,
    element: '[data-tour="dashboard-cliente"]',
    title: 'La tua dashboard',
    description: 'Panoramica in tempo reale dei tuoi servizi: firewall, backup, sicurezza, ticket.',
    side: 'bottom',
    align: 'center',
  },
  {
    path: paths.dashboard,
    element: '[data-tour="tickets-cliente"]',
    title: 'I tuoi ticket',
    description: 'Apri una richiesta di supporto, segui lo stato, rispondi agli operatori.',
    side: 'bottom',
    align: 'center',
  },
  {
    path: paths.dashboard,
    element: '[data-tour="sicurezza"]',
    title: 'Sicurezza in tempo reale',
    description: 'Attacchi bloccati, tentativi di accesso, eventi critici. Aggiornati continuamente.',
    side: 'top',
    align: 'center',
  },
  {
    path: documentiCliente,
    element: '[data-tour="documenti"]',
    title: 'I tuoi documenti',
    description:
      'Cosa cambia: dai numeri dei servizi ai file condivisi con la tua azienda. Li apri da qui.',
    side: 'bottom',
    align: 'start',
  },
  {
    path: documentiCliente,
    element: '[data-tour="profile-menu"]',
    title: 'Impostazioni',
    description: 'Il tuo profilo, le notifiche, e il pulsante per rivedere questo tour.',
    side: 'bottom',
    align: 'end',
  },
];

/**
 * Company operators never land on the service dashboard: `/` sends them to the
 * tickets they are working on. Alert and internal docs stay staff-only.
 */
export const TOUR_CLIENTE_USER: TourStep[] = [
  {
    path: paths.ticket.inLavorazione,
    element: '[data-tour="tickets-cliente"]',
    title: 'I tuoi ticket',
    description: 'Le richieste in lavorazione. Apri una riga per seguire lo stato e rispondere.',
    side: 'bottom',
    align: 'center',
  },
  {
    path: documentiCliente,
    element: '[data-tour="documenti"]',
    title: 'I tuoi documenti',
    description: 'Cosa cambia: dai ticket ai file condivisi con la tua azienda. Li apri da qui.',
    side: 'bottom',
    align: 'start',
  },
  {
    path: documentiCliente,
    element: '[data-tour="profile-menu"]',
    title: 'Impostazioni',
    description: 'Il tuo profilo, le notifiche, e il pulsante per rivedere questo tour.',
    side: 'bottom',
    align: 'end',
  },
];

export function tourRoleFor(ruolo: string | null | undefined): TourRole {
  return ruolo === 'cliente_user' || ruolo === 'cliente_admin' ? 'cliente' : 'staff';
}

export function stepsFor(ruolo: string | null | undefined): TourStep[] {
  if (ruolo === 'cliente_user') return TOUR_CLIENTE_USER;
  if (ruolo === 'cliente_admin') return TOUR_CLIENTE;
  return TOUR_STAFF;
}

export function stepsForRole(role: TourRole): TourStep[] {
  return role === 'staff' ? TOUR_STAFF : TOUR_CLIENTE;
}

/** First match that is actually on screen. Hidden responsive duplicates are ignored. */
export function visibleTourTarget(selector: string): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const nodes = document.querySelectorAll(selector);
  for (const node of nodes) {
    if (!(node instanceof HTMLElement)) continue;
    const style = window.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden') continue;
    const rect = node.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) continue;
    return node;
  }
  return null;
}
